import { parseDate, today } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { ExternalLinkIcon, MoreVertical, PencilIcon } from "lucide-react";
import { useState } from "react";
import type z from "zod";

import { roleHasPermission } from "@/auth/shared";
import { Button } from "@/components/base/button";
import { Checkbox } from "@/components/base/checkbox";
import type { DataTableColumn } from "@/components/base/data-table";
import { DataTable } from "@/components/base/data-table";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuItemLink,
} from "@/components/base/dropdown-menu";
import { useAppForm } from "@/components/base/form";
import { Link } from "@/components/base/link";
import { Pagination } from "@/components/base/pagination";
import { title } from "@/components/base/primitives";
import { Toolbar } from "@/components/base/toolbar";
import { ManageRegistrationForm } from "@/components/tournaments/controls/manage-registration";
import { MessagePlayersForm } from "@/components/tournaments/controls/message-players";
import { SanctionBadge } from "@/components/tournaments/sanction-badge";
import {
  getTournamentsByDirectorsOptions,
  getTournamentsByDirectorsSchema,
} from "@/functions/tournaments/get-tournaments-by-directors";
import { updateDivisionBatchSchema } from "@/functions/tournaments/update-division-batch";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useCopyPlayerEmails } from "@/hooks/tournament-mutations";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";
import { getDefaultTimeZone } from "@/lib/dates";

export const Route = createFileRoute("/td/")({
  validateSearch: getTournamentsByDirectorsSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ context: { queryClient, ...context }, deps: search }) => {
    const viewer = context.viewer;

    const hasPermission =
      viewer &&
      roleHasPermission(viewer.role, {
        tournaments: ["update"],
      });

    if (!hasPermission) {
      throw redirect({ to: "/not-found" });
    }

    queryClient.prefetchQuery(
      getTournamentsByDirectorsOptions({
        past: search.past,
        page: search.page,
        pageSize: search.pageSize,
      }),
    );
  },
  component: RouteComponent,
});

type Tournament = {
  id: number;
  name: string | null;
  date: string;
  visible: boolean;
  demo: boolean;
  venue: { id: number; name: string; city: string };
  tournamentDivisions: {
    id: number;
    name: string | null;
    gender: string;
    teamSize: number;
    displayGender: string | null;
    displayDivision: string | null;
    rosterPublished: boolean;
    capacity: number;
    waitlistCapacity: number;
    division: { name: string; display: string | null; maxAge: number | null };
    teams: { id: number; status: string }[];
  }[];
};

function RouteComponent() {
  const search = Route.useSearch();

  const navigate = useNavigate();

  const {
    data: tournamentsResponse,
    refetch,
    isLoading,
  } = useQuery(
    getTournamentsByDirectorsOptions({
      past: search.past,
      page: search.page,
      pageSize: search.pageSize,
    }),
  );

  const tournaments =
    (tournamentsResponse?.data as Tournament[] | undefined) ?? [];
  const pageInfo = tournamentsResponse?.pageInfo;

  const dateFormatter = useDateFormatter({
    dateStyle: "short",
    timeZone: getDefaultTimeZone(),
  });

  const trpc = useTRPC();

  const { mutate: copyAsDemo } = useMutation({
    ...trpc.tournaments.createDemoTournament.mutationOptions(),
    onSuccess: ({ data }) => {
      navigate({
        to: "/tournaments/$tournamentId",
        params: {
          tournamentId: data.id.toString(),
        },
      });
    },
  });

  const { mutate: deleteDemo } = useMutation({
    ...trpc.tournaments.deleteDemoTournament.mutationOptions(),
    onSuccess: () => {
      refetch();
    },
  });

  const [manageRegistrationFormId, setManageRegistrationFormId] = useState<
    number | undefined
  >();
  const [messagePlayersTournament, setMessagePlayersTournament] = useState<
    Tournament | undefined
  >();

  const handleCopyEmails = useCopyPlayerEmails();

  const columns: DataTableColumn<Tournament>[] = [
    {
      id: "date",
      header: "Date",
      width: 110,
      minWidth: 110,
      cell: (tournament) => (
        <Link
          variant="alt"
          to="/tournaments/$tournamentId"
          params={{ tournamentId: tournament.id.toString() }}
          className="flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {dateFormatter.format(
            parseDate(tournament.date).toDate(getDefaultTimeZone()),
          )}
          <ExternalLinkIcon size={14} className="shrink-0" />
        </Link>
      ),
    },
    {
      id: "event",
      header: "Event",
      isRowHeader: true,
      minWidth: 100,
      cell: (tournament) => (
        <span className="block truncate whitespace-nowrap">
          {tournament.name?.trim() ||
            `${tournament.tournamentDivisions.length} division${tournament.tournamentDivisions.length !== 1 ? "s" : ""}`}
        </span>
      ),
    },
    {
      id: "location",
      header: "Location",
      minWidth: 100,
      cell: (tournament) => (
        <span className="block truncate whitespace-nowrap">
          {tournament.venue.name}
          <span className="hidden md:inline">, {tournament.venue.city}</span>
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      width: 48,
      minWidth: 48,
      cell: (tournament) => (
        <>
          <DropdownMenu
            label="Tournament actions"
            buttonIcon={<MoreVertical size={16} />}
          >
            <DropdownMenuItem onPress={() => handleCopyEmails(tournament.id)}>
              Copy Player Emails
            </DropdownMenuItem>
            <DropdownMenuItem
              onPress={() => setMessagePlayersTournament(tournament)}
            >
              Message Players
            </DropdownMenuItem>
            <DropdownMenuItem
              onPress={() => setManageRegistrationFormId(tournament.id)}
            >
              Pause Registrations
            </DropdownMenuItem>
            <DropdownMenuItemLink
              to="/tournaments/$tournamentId"
              params={{ tournamentId: tournament.id.toString() }}
            >
              View Tournament
            </DropdownMenuItemLink>
            {tournament.demo ? (
              <DropdownMenuItem
                onAction={() => {
                  deleteDemo({ id: tournament.id });
                }}
              >
                Delete Demo
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onAction={() => {
                  copyAsDemo({ id: tournament.id });
                }}
              >
                Copy as Demo
              </DropdownMenuItem>
            )}
          </DropdownMenu>
        </>
      ),
    },
  ];

  return (
    <DefaultLayout
      classNames={{
        content:
          "flex flex-col space-y-12 w-full max-w-full md:max-w-2xl px-3 py-12 mx-auto",
      }}
    >
      <div className="flex flex-col space-y-2">
        <div className="flex items-center justify-between">
          <h2 className={title({ size: "sm" })}>Your Tournaments</h2>
        </div>

        <Toolbar className="self-end">
          <Checkbox
            label="Previous"
            isSelected={search.past}
            onChange={(value) => {
              navigate({
                to: "/td",
                search: (prev) => ({
                  ...prev,
                  past: value,
                  page: 1,
                }),
              });
            }}
          />
        </Toolbar>

        <div className="flex max-w-full flex-col items-center space-y-6 overflow-hidden">
          <DataTable
            aria-label="Your tournaments"
            data={tournaments}
            columns={columns}
            getRowId={(t) => t.id}
            renderExpandedContent={(tournament) => (
              <TournamentDivisions tournament={tournament} />
            )}
            renderEmptyState={() => (
              <div className="p-4 text-center text-gray-500 italic">
                {isLoading ? "Loading tournaments..." : "No tournaments found."}
              </div>
            )}
          />

          {pageInfo && pageInfo.totalPages > 1 && (
            <Pagination
              to="/td/"
              page={search.page}
              pageSize={search.pageSize}
              pageInfo={pageInfo}
            />
          )}
        </div>
      </div>
      {manageRegistrationFormId && (
        <ManageRegistrationForm
          tournamentId={manageRegistrationFormId}
          isOpen={true}
          onOpenChange={setManageRegistrationFormId}
        />
      )}
      {messagePlayersTournament && (
        <MessagePlayersForm
          tournamentId={messagePlayersTournament.id}
          divisions={messagePlayersTournament.tournamentDivisions}
          isDemo={messagePlayersTournament.demo}
          isOpen={true}
          onOpenChange={() => setMessagePlayersTournament(undefined)}
        />
      )}
    </DefaultLayout>
  );
}

function TournamentDivisions({ tournament }: { tournament: Tournament }) {
  const [editing, setEditing] = useState(false);
  const isInFuture =
    parseDate(tournament.date).compare(today(getDefaultTimeZone())) > 0;

  return (
    <div className="flex flex-col gap-2 p-3">
      {!editing && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Divisions</span>
          {isInFuture && (
            <Button size="sm" onPress={() => setEditing((v) => !v)}>
              <PencilIcon size={12} />
              Edit
            </Button>
          )}
        </div>
      )}

      {editing ? (
        <InlineManageDivisions
          tournament={tournament}
          onDone={() => setEditing(false)}
        />
      ) : (
        <DivisionsList tournament={tournament} />
      )}
    </div>
  );
}

function DivisionsList({ tournament }: { tournament: Tournament }) {
  const trpc = useTRPC();

  const { data: teamStats } = useQuery(
    trpc.tournaments.getTournamentTeamStats.queryOptions({
      tournamentId: tournament.id,
    }),
  );

  return (
    <>
      {tournament.tournamentDivisions.map((td) => {
        const registered = td.teams.filter(
          (t) => t.status === "registered" || t.status === "confirmed",
        ).length;
        const waitlisted = td.teams.filter(
          (t) => t.status === "waitlisted",
        ).length;
        const stats = teamStats?.find((s) => s.divisionId === td.id);

        return (
          <div
            key={td.id}
            className="flex min-w-0 flex-col gap-0.5 text-sm md:flex-row md:items-center md:justify-between md:gap-2"
          >
            <Link
              variant="alt"
              to="/tournaments/$tournamentId/$divisionId"
              params={{
                tournamentId: tournament.id.toString(),
                divisionId: td.id.toString(),
              }}
              onClick={(e) => e.stopPropagation()}
              className="truncate"
            >
              {getTournamentDivisionDisplay(td)}
            </Link>
            <div className="flex flex-col items-start gap-0.5 md:items-end">
              <div className="flex items-center gap-2">
                {stats && (
                  <SanctionBadge
                    sanction={stats.sanction}
                    divisionName={td.division.name}
                  />
                )}
                <Link
                  variant="alt"
                  to="/tournaments/$tournamentId/$divisionId/{-$tab}"
                  params={{
                    tournamentId: tournament.id.toString(),
                    divisionId: td.id.toString(),
                    tab: "teams",
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs whitespace-nowrap text-gray-500 md:text-sm"
                >
                  {registered}/{td.capacity} registered,{" "}
                  <span className="text-amber-600">
                    {waitlisted}/{td.waitlistCapacity} waitlist
                  </span>
                </Link>
              </div>
              {stats && (
                <span className="text-xs whitespace-nowrap text-gray-400 md:text-sm">
                  {stats.active} active, {stats.withdrawn} withdrawn,{" "}
                  {stats.paid} paid (not refunded or comped)
                </span>
              )}
            </div>
          </div>
        );
      })}
      {tournament.tournamentDivisions.length === 0 && (
        <span className="text-sm text-gray-400 italic">No divisions</span>
      )}
    </>
  );
}

function InlineManageDivisions({
  tournament,
  onDone,
}: {
  tournament: Tournament;
  onDone: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.updateDivisionBatch.mutationOptions(),
    onSuccess: (_data, variables) => {
      queryClient.setQueriesData(
        { queryKey: ["getTournamentsByDirectors"] },
        (old: { data: Tournament[]; pageInfo: unknown } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((t) => {
              if (t.id !== tournament.id) return t;

              return {
                ...t,
                tournamentDivisions: t.tournamentDivisions.map((td) => {
                  const update = variables.divisions.find(
                    (d) => d.id === td.id,
                  );
                  if (!update) return td;
                  return {
                    ...td,
                    capacity: update.capacity,
                    waitlistCapacity: update.waitlistCapacity,
                  };
                }),
              };
            }),
          };
        },
      );

      queryClient.invalidateQueries({
        queryKey: ["getTournamentsByDirectors"],
      });

      onDone();
    },
  });

  const schema = updateDivisionBatchSchema;

  const form = useAppForm({
    defaultValues: {
      divisions: tournament.tournamentDivisions.map(
        ({ id, capacity, waitlistCapacity }) => ({
          id,
          capacity,
          waitlistCapacity,
        }),
      ),
    } as z.infer<typeof schema>,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: ({ value: { divisions }, formApi }) => {
      mutate({ divisions }, { onSuccess: () => formApi.reset() });
    },
  });

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {failureReason && (
        <form.AppForm>
          <form.Alert title="Error" description={failureReason.message} />
        </form.AppForm>
      )}

      <div className="hidden grid-cols-6 gap-2 text-xs text-gray-500 md:grid">
        <span className="col-span-2">Division</span>
        <span className="col-span-2">Capacity</span>
        <span className="col-span-2">WL Capacity</span>
      </div>

      <form.AppField name="divisions" mode="array">
        {(field) =>
          field.state.value?.map(({ id }, i) => {
            const division = tournament.tournamentDivisions.find(
              (d) => d.id === id,
            );
            if (!division) return null;

            return (
              <div
                key={id}
                className="flex flex-col gap-1 md:grid md:grid-cols-6 md:items-center md:gap-2"
              >
                <div className="text-sm font-medium md:col-span-2 md:font-normal">
                  {getTournamentDivisionDisplay(division)}
                </div>
                <div className="grid grid-cols-2 gap-1 md:contents">
                  <form.AppField name={`divisions[${i}].capacity`}>
                    {(subField) => (
                      <subField.Number
                        field={subField}
                        label="Capacity"
                        className="md:col-span-2 [&>label]:md:hidden"
                      />
                    )}
                  </form.AppField>
                  <form.AppField name={`divisions[${i}].waitlistCapacity`}>
                    {(subField) => (
                      <subField.Number
                        field={subField}
                        label="WL Capacity"
                        className="md:col-span-2 [&>label]:md:hidden"
                      />
                    )}
                  </form.AppField>
                </div>
              </div>
            );
          })
        }
      </form.AppField>

      <form.AppForm>
        <form.Footer>
          <Button onPress={onDone}>Cancel</Button>
          <form.SubmitButton>Save</form.SubmitButton>
        </form.Footer>
      </form.AppForm>
    </form>
  );
}
